class DraggingImageCreator {
  constructor(scaleFactor = 1, itemIdFn = (el) => el.id) {
    this.scaleFactor = scaleFactor;
    this.itemIdFn = itemIdFn;
    this.containerGap = 50;
    this.stackedImageDistance = 10;
    this.maxStackedImages = 5;

    this.draggingImages = {};

    this.createDraggingImageForElement = this.createDraggingImageForElement.bind(this);
  }

  createDraggingImageForElement(element) {
    const elementId = this.itemIdFn(element);
    const oldGhost = this.draggingImages[elementId];
    if (oldGhost) {
      oldGhost.remove();
      delete this.draggingImages[elementId];
    }

    const rect = element.getBoundingClientRect();

    const ghost = document.createElement('div');
    Object.assign(ghost.style, {
      position: 'absolute',
      left: '0px',
      top: '0px',
      width: rect.width + this.containerGap * this.scaleFactor + 'px',
      height: rect.height + this.containerGap * this.scaleFactor + 'px',
      margin: '0px',
      padding: `${this.containerGap}px`,
      pointerEvents: 'none',
      opacity: '1',
      visibility: 'hidden',
    });

    const isWindows = navigator.userAgent.includes('Windows');
    const inner = element.cloneNode(true);
    inner.classList.add('draggable-ghost');
    isWindows && inner.classList.add('windows');

    Object.assign(inner.style, {
      transform: `scale(${this.scaleFactor})`,
      transformOrigin: 'top left',
      opacity: '1',
    });

    ghost.appendChild(inner);
    document.body.appendChild(ghost);

    this.draggingImages[elementId] = ghost;
    return ghost;
  }

  getDraggingImageForElement(element) {
    const elementId = this.itemIdFn(element);
    return this.draggingImages[elementId];
  }

  setStackedDraggingImages(event, elements) {
    // Ensure ghosts exist for all elements
    elements.forEach((el) => {
      if (!this.getDraggingImageForElement(el)) {
        this.createDraggingImageForElement(el);
      }
    });

    const compositeImage = this.#buildCompositeStackedImage(elements);
    if (!compositeImage) {
      return;
    }

    const offsetX = ((event.offsetX ?? 0) + this.containerGap) * this.scaleFactor;
    const offsetY = ((event.offsetY ?? 0) + this.containerGap) * this.scaleFactor;

    event.dataTransfer.setDragImage(compositeImage, offsetX, offsetY);
  }

  hideDraggingImage(element) {
    const ghostElement = this.draggingImages[this.itemIdFn(element)];
    if (!ghostElement) {
      return;
    }

    if (ghostElement.parentNode && ghostElement.parentNode !== document.body) {
      document.body.appendChild(ghostElement);
    }

    ghostElement.style.top = '0';
    ghostElement.style.left = '-9999px';
    ghostElement.style.visibility = 'hidden';
  }

  destroyDraggingImages() {
    Object.values(this.draggingImages).forEach((el) => el.remove());
    this.draggingImages = {};
    if (this.compositeImage) {
      this.compositeImage.remove();
      this.compositeImage = null;
    }
  }

  #buildCompositeStackedImage(elements) {
    if (this.compositeImage) {
      this.compositeImage.remove();
      this.compositeImage = null;
    }

    const ghosts = elements.map((el) => this.getDraggingImageForElement(el)).filter(Boolean);

    const count = Math.min(ghosts.length, this.maxStackedImages);
    if (count === 0) {
      return null;
    }

    const baseRect = ghosts[0].getBoundingClientRect();
    const width = baseRect.width + this.containerGap * this.scaleFactor;
    const height = baseRect.height + this.containerGap * this.scaleFactor;

    const wrapper = document.createElement('div');
    Object.assign(wrapper.style, {
      position: 'fixed',
      left: '-9999px',
      top: '0',
      width: width + 'px',
      height: height + 'px',
      padding: `${this.containerGap}px`,
      pointerEvents: 'none',
      zIndex: '999999',
    });

    if (elements.length > 1) {
      wrapper.appendChild(this.#counterDiv(elements));
    }

    for (let i = 0; i < count; i++) {
      const el = ghosts[count - 1 - i];

      Object.assign(el.style, {
        left: `${-this.stackedImageDistance * i}px`,
        top: `${-this.stackedImageDistance * i}px`,
        visibility: 'visible',
      });

      wrapper.appendChild(el);
    }

    document.body.appendChild(wrapper);
    this.compositeImage = wrapper;
    return wrapper;
  }

  #showDraggingImage(ghostElement) {
    ghostElement.style.top = '0px';
    ghostElement.style.left = '-9999px';
    ghostElement.style.visibility = 'visible';
  }

  #counterDiv(elements) {
    const counterDiv = document.createElement('div');
    counterDiv.classList.add('designs-dragged-count');
    counterDiv.innerHTML = elements.length;

    return counterDiv;
  }
}

export default DraggingImageCreator;
